import React, { useMemo } from 'react';
import useTranslation from '~/app/hooks/useTranslation';
import Icon from '~/app/common/Icon';
import { WorkOrderDetail } from '~/workorder/types';
import { canEditWorkOrderCommentAndDescription } from '../MainContent/helper';
import { useWorkOrderTextAutoSave } from '../useWorkOrderTextAutoSave';
import classes from './style.module.scss';
import Textarea from '~/app/modules/form/Textarea';

export interface CommentProps {
    workOrderId: WorkOrderDetail['id'];
    status: WorkOrderDetail['status'];
    comment: string;
}

export type CommentComponent = React.FC<CommentProps>;

const Comment: CommentComponent = ({ workOrderId, status, comment }) => {
    const { t } = useTranslation('workorder');

    const body = useMemo(() => {
        const text = comment?.trim();
        return text || '-';
    }, [comment]);

    const canEdit = useMemo(() => canEditWorkOrderCommentAndDescription(status), [status]);

    const { isEditing, draft, setDraft, startEditing, handleBlur, saving, textareaRef } = useWorkOrderTextAutoSave({
        workOrderId,
        fieldKey: 'comment',
        savedValue: comment,
        canEdit,
    });

    return (
        <div className={classes['card']}>
            <div className={classes['header']}>
                <h2 className={classes['header-title']}>
                    <Icon name="forum" size={20} className={classes['header-icon']} />
                    {t('detail.comments')}
                </h2>
                {canEdit && !isEditing && (
                    <button
                        type="button"
                        className={classes['edit-btn']}
                        aria-label={t('detail.edit')}
                        disabled={saving}
                        onClick={startEditing}
                    >
                        <Icon name="edit" size={16} />
                        {t('detail.edit')}
                    </button>
                )}
            </div>
            {isEditing ? (
                <>
                    <Textarea
                        ref={textareaRef}
                        value={draft}
                        disabled={saving}
                        onChange={e => setDraft(e.target.value)}
                        onBlur={handleBlur}
                    />
                </>
            ) : (
                <p className={classes['body']}>{body}</p>
            )}
        </div>
    );
};

export default Comment;
